import { Type, type Static, type TLiteral, type TObject, type TProperties } from 'typebox';
import { Value } from 'typebox/value';

/* The app-to-twin contract: one versioned envelope, typed commands from the app (or a page embedding
 * the twin) and typed events from the viewer. Both sides validate what they receive; anything else is
 * ignored. The Dart side of the app can be generated from the JSON schemas exported here. */

export const BRIDGE = 'budovan-twin';
export const BRIDGE_VERSION = 1;

const PlaceCode = Type.String({ pattern: '^[A-Z][A-Z0-9-]{1,39}$' });
type MessageSchema<K extends string, P extends TProperties> = TObject<{
  bridge: TLiteral<typeof BRIDGE>;
  v: TLiteral<typeof BRIDGE_VERSION>;
  kind: TLiteral<'command' | 'event'>;
  type: TLiteral<K>;
  payload: TObject<P>;
}>;

const message = <K extends string, P extends TProperties>(
  kind: 'command' | 'event',
  type: K,
  payload: P,
): MessageSchema<K, P> =>
  Type.Object({
    bridge: Type.Literal(BRIDGE),
    v: Type.Literal(BRIDGE_VERSION),
    kind: Type.Literal(kind),
    type: Type.Literal(type),
    payload: Type.Object(payload),
  });

export const Command = Type.Union([
  message('command', 'showPlace', { code: PlaceCode }),
  /** A floor number cuts the building above it; null shows every floor. */
  message('command', 'showFloor', { number: Type.Union([Type.Integer(), Type.Null()]) }),
  /** The whole building again, from the side or from above (the 3D and recenter map buttons). */
  message('command', 'setView', { mode: Type.Enum(['3d', 'top']) }),
  /** Name tags with a status dot over these places; an empty list removes them. */
  message('command', 'setLabels', { codes: Type.Array(PlaceCode, { maxItems: 2000 }) }),
  /** Drops the selected place (the app closed its card). */
  message('command', 'clearSelection', {}),
  message('command', 'highlight', { codes: Type.Array(PlaceCode, { maxItems: 500 }) }),
  message('command', 'setAccessToken', { token: Type.String({ minLength: 10, maxLength: 8000 }) }),
  /** Free and booked places for a range the app is booking in; they override the live state until cleared. */
  message('command', 'setAvailability', {
    free: Type.Array(PlaceCode, { maxItems: 2000 }),
    booked: Type.Array(PlaceCode, { maxItems: 2000 }),
  }),
  message('command', 'setFilter', {
    bookingType: Type.Union([Type.Enum(['space', 'desk', 'event', 'parking']), Type.Null()]),
  }),
]);
export type Command = Static<typeof Command>;

export const Event = Type.Union([
  message('event', 'ready', {
    bridgeVersion: Type.Integer(),
    buildingCode: Type.String(),
    mode: Type.Enum(['public', 'signed_in']),
  }),
  message('event', 'placeTapped', { code: PlaceCode }),
  message('event', 'floorChanged', { number: Type.Union([Type.Integer(), Type.Null()]) }),
  message('event', 'error', { code: Type.String(), message: Type.String() }),
]);
export type Event = Static<typeof Event>;

type PayloadOf<U, T> = U extends { type: T; payload: infer P } ? P : never;

export const command = <T extends Command['type']>(type: T, payload: PayloadOf<Command, T>): Command =>
  ({ bridge: BRIDGE, v: BRIDGE_VERSION, kind: 'command', type, payload }) as Command;

export const event = <T extends Event['type']>(type: T, payload: PayloadOf<Event, T>): Event =>
  ({ bridge: BRIDGE, v: BRIDGE_VERSION, kind: 'event', type, payload }) as Event;

const parse = (data: unknown): unknown => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

/** A command from the app, or null when the data is anything else (another sender, another version). */
export const parseCommand = (data: unknown): Command | null => {
  const value = parse(data);
  return Value.Check(Command, value) ? value : null;
};

export const parseEvent = (data: unknown): Event | null => {
  const value = parse(data);
  return Value.Check(Event, value) ? value : null;
};
